import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbJsonLd } from "../components/Seo";
import { PAGE_SEO, WA_LINKS } from "../data/siteData";
import { CASE_STUDY_FORM_URL } from "../config";
import { ArrowUpRight } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "experience-blueprint", label: "Experience Blueprint™" },
  { id: "character-branding", label: "Character Branding™" },
];

// Case studies are rendered as full-width premium scroll galleries.
const CASE_STUDIES = [
  {
    id: "executive-grooming",
    label: "Executive Grooming",
    subLabel: "Barbershop · Nassau, The Bahamas",
    category: "experience-blueprint",
    pages: [
      { id: "cover", src: "/assets/case-studies/eg-cover.png", alt: "Executive Grooming — Case Study Cover" },
      { id: "page-1", src: "/assets/case-studies/eg-page-1.png", alt: "Executive Grooming — Frontage Analysis" },
      { id: "page-2", src: "/assets/case-studies/eg-page-2.png", alt: "Executive Grooming — Interior Atmosphere" },
      { id: "page-3", src: "/assets/case-studies/eg-page-3.png", alt: "Executive Grooming — Concept Visualization" },
    ],
  },
  {
    id: "herbs-seafood-market",
    label: "Herb's Seafood Market",
    subLabel: "Seafood Market · Nassau, The Bahamas",
    category: "experience-blueprint",
    pages: [
      { id: "cover", src: "/assets/case-studies/herbs-cover.png", alt: "Herb's Seafood Market — Case Study Cover" },
      { id: "page-2", src: "/assets/case-studies/herbs-page-2.png", alt: "Herb's Seafood Market — First Impression Matters (Exterior)" },
      { id: "page-3", src: "/assets/case-studies/herbs-page-3.png", alt: "Herb's Seafood Market — First Impression (Interior)" },
      { id: "page-4", src: "/assets/case-studies/herbs-page-4.png", alt: "Herb's Seafood Market — Counter Experience Before & After" },
      { id: "page-5", src: "/assets/case-studies/herbs-page-5.png", alt: "Herb's Seafood Market — The Blueprint Is Complete" },
    ],
  },
  {
    id: "wig-doctor",
    label: "The Wig Doctor",
    subLabel: "Character Branding · Featuring Dr. Mully",
    category: "character-branding",
    pages: [
      { id: "page-1", src: "/assets/case-studies/wig-doctor-page-1.png", alt: "The Wig Doctor — Case Study Cover featuring Dr. Mully" },
      { id: "page-2", src: "/assets/case-studies/wig-doctor-page-2.png", alt: "The Wig Doctor — From Idea to Icon" },
    ],
  },
  {
    id: "authentic-loft",
    label: "The Authentic Loft",
    subLabel: "Character Branding · Featuring Arielle",
    category: "character-branding",
    pages: [
      { id: "cover", src: "/assets/case-studies/loft-cover.png", alt: "The Authentic Loft — Character Branding Case Study Cover featuring Arielle" },
      { id: "page-2", src: "/assets/case-studies/loft-page-2.png", alt: "The Authentic Loft — Arielle, the Loft Ambassador profile and apparel showcase" },
      { id: "page-3", src: "/assets/case-studies/loft-page-3.png", alt: "The Authentic Loft — Branded business cards featuring Arielle" },
      { id: "page-4", src: "/assets/case-studies/loft-page-4.png", alt: "The Authentic Loft — Style Authentically brand flyer" },
      { id: "page-5", src: "/assets/case-studies/loft-page-5.png", alt: "AXIS Character Branding — Get Your Brand Ambassador Today promo" },
    ],
  },
];

const CaseStudies = () => {
  const [activeId, setActiveId] = useState(CASE_STUDIES[0].id);
  const [filter, setFilter] = useState("all");

  const visibleStudies =
    filter === "all" ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.category === filter);

  const handleFilter = (id) => {
    setFilter(id);
    const visible = id === "all" ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.category === id);
    if (!visible.some((c) => c.id === activeId)) {
      setActiveId(visible[0].id);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Re-run reveal observer whenever the active case study changes.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
      el.classList.remove("is-visible");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [activeId]);

  const active = CASE_STUDIES.find((c) => c.id === activeId) ?? CASE_STUDIES[0];

  return (
    <div data-testid="case-studies-page" className="bg-[#0A192F]">
      <Seo
        {...PAGE_SEO.caseStudies}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
        ])}
      />
      <Navbar />

      {/* HEADER */}
      <section className="relative bg-[#0A192F] text-white pt-32 pb-14 md:pt-40 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(192,192,192,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(192,192,192,0.06) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              Case Studies
            </span>
          </div>
          <h1
            data-testid="cs-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter font-semibold leading-[1.0]"
          >
            Real Businesses.
            <br />
            <span className="text-[#D4AF37]">Real Transformations.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
            Each AXIS engagement begins with observation and ends with a transformed customer
            experience. Choose a client below to walk through their journey, page by page.
          </p>

          {/* FILTER CHIPS */}
          <div
            data-testid="cs-filter-chips"
            className="mt-8 flex flex-wrap items-center gap-2.5"
            role="group"
            aria-label="Filter case studies by service"
          >
            {FILTERS.map((f) => {
              const isActive = f.id === filter;
              return (
                <button
                  key={f.id}
                  data-testid={`cs-filter-${f.id}`}
                  aria-pressed={isActive}
                  onClick={() => handleFilter(f.id)}
                  className={`rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-semibold border transition-all duration-300 ${
                    isActive
                      ? "bg-[#D4AF37] text-[#0A192F] border-[#D4AF37]"
                      : "bg-transparent text-[#C0C0C0] border-[#C0C0C0]/30 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* TAB SWITCHER */}
      <section
        data-testid="cs-tabs"
        className="sticky top-0 z-30 bg-[#0A192F]/95 backdrop-blur-md border-y border-[#C0C0C0]/15"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div
            role="tablist"
            aria-label="Case studies"
            className="flex items-stretch gap-2 md:gap-6 overflow-x-auto scrollbar-hide"
          >
            {visibleStudies.map((cs) => {
              const isActive = cs.id === activeId;
              return (
                <button
                  key={cs.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`cs-tab-${cs.id}`}
                  onClick={() => {
                    setActiveId(cs.id);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={`relative py-5 md:py-6 px-2 md:px-4 text-left flex-shrink-0 transition-colors duration-300 group ${
                    isActive ? "text-white" : "text-[#94A3B8] hover:text-white"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">
                    <span className={isActive ? "text-[#D4AF37]" : "text-[#94A3B8] group-hover:text-[#D4AF37]"}>
                      Case Study 0{CASE_STUDIES.indexOf(cs) + 1}
                    </span>
                  </div>
                  <div className="font-display text-base md:text-xl font-semibold whitespace-nowrap">
                    {cs.label}
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5 hidden md:block">{cs.subLabel}</div>
                  <span
                    className={`absolute left-0 right-0 bottom-0 h-[2px] transition-all duration-500 ${
                      isActive ? "bg-[#D4AF37]" : "bg-transparent group-hover:bg-[#D4AF37]/30"
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CASE STUDY PAGES */}
      <section
        key={active.id}
        data-testid={`cs-active-${active.id}`}
        className="relative bg-[#0A192F] pt-14 md:pt-20 pb-24 md:pb-32"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 space-y-10 md:space-y-16">
          {active.pages.map((page, i) => (
            <figure
              key={`${active.id}-${page.id}`}
              data-testid={`cs-page-${active.id}-${page.id}`}
              className="reveal-on-scroll group"
            >
              <div className="relative bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-[#D4AF37]/20 overflow-hidden">
                <img
                  src={page.src}
                  alt={page.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="block w-full h-auto"
                />
              </div>
              <figcaption className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] font-semibold px-1">
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#D4AF37] rotate-45" />
                  {active.label}
                </span>
                <span>
                  {String(i + 1).padStart(2, "0")} / {String(active.pages.length).padStart(2, "0")}
                </span>
              </figcaption>
            </figure>
          ))}

          {/* GET RESULTS LIKE THIS — banner at the end of every gallery */}
          <div
            data-testid={`cs-results-cta-${active.id}`}
            className="reveal-on-scroll relative overflow-hidden bg-[#0A192F] border border-[#D4AF37]/40 px-8 py-12 md:px-14 md:py-16 text-center"
          >
            <div
              className="absolute inset-0 opacity-50 pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(60% 90% at 15% 20%, rgba(212,175,55,0.22) 0%, transparent 60%), radial-gradient(50% 70% at 90% 85%, rgba(30,58,138,0.5) 0%, transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                  Your Business Could Be Next
                </span>
                <span className="h-px w-8 bg-[#D4AF37]" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight font-semibold text-white leading-[1.1]">
                Get Results Like This.
              </h3>
              <p className="mt-4 text-sm md:text-base text-[#C0C0C0] leading-relaxed max-w-xl mx-auto">
                You&apos;ve just seen what happened for {active.label}. Tell us about your
                business — it takes two minutes — and we&apos;ll show you what&apos;s possible.
              </p>
              <a
                href={WA_LINKS.recommend}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cs-results-cta-btn-${active.id}`}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-8 py-4 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
              >
                Get My AXIS Recommendation <ArrowUpRight className="w-4 h-4" />
              </a>
              <div className="mt-4">
                <a
                  href={CASE_STUDY_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cs-results-form-${active.id}`}
                  className="text-xs text-[#94A3B8] underline underline-offset-4 hover:text-[#D4AF37] transition-colors"
                >
                  Prefer a form? Check my fit
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-white py-24 md:py-32 border-t border-[#C0C0C0]/40">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
            See what an Experience Blueprint™
            <br />
            <span className="text-[#1E3A8A]">can do for your business.</span>
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services/experience-blueprint"
              data-testid="cs-cta-learn"
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-7 py-3.5 text-sm md:text-base font-semibold border border-[#0A192F] hover:bg-transparent hover:text-[#0A192F] transition-all duration-300"
            >
              Learn About The Blueprint <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={WA_LINKS.blueprint}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cs-cta-quick-look"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-3.5 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
            >
              Start With an AXIS Quick Look →
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .reveal-on-scroll { opacity: 0; transform: translateY(28px); transition: opacity 0.9s ease, transform 0.9s ease; }
        .reveal-on-scroll.is-visible { opacity: 1; transform: translateY(0); }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default CaseStudies;
